import { Link } from 'react-router-dom';
import { Sparkles, Wifi, Activity, Shield, Cpu, ArrowRight } from 'lucide-react';

export default function Landing() {
  return (
    <div className="min-h-full bg-bg">
      {/* Header */}
      <header className="border-b border-border">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center gap-3">
          <div className="w-5 h-5 rounded-full bg-brand animate-firefly" />
          <span className="font-semibold tracking-tight">Filos<span className="text-text-faint font-normal"> · NMS</span></span>
          <nav className="ml-auto flex items-center gap-1">
            <a href="#features" className="btn btn-ghost btn-sm">Fonctionnalités</a>
            <a href="#stack" className="btn btn-ghost btn-sm">Stack</a>
            <Link to="/login" className="btn btn-primary btn-sm">Se connecter <ArrowRight size={11} /></Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-border">
        <div className="max-w-6xl mx-auto px-6 py-20 text-center">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 mb-5 text-xs bg-brand-dim border border-brand/20 text-brand rounded-full">
            <Sparkles size={11} /> Une API pour gérer tout le réseau ISP
          </div>
          <h1 className="text-4xl md:text-5xl font-semibold tracking-tight max-w-3xl mx-auto leading-tight">
            Une seule API pour piloter <span className="text-brand">tout ton réseau</span>.
          </h1>
          <p className="mt-5 text-text-muted max-w-2xl mx-auto text-base">
            Filos NMS normalise 90% des opérations OLT / ONT / CPE derrière une API unique.
            Calix, Adtran, Nokia, Huawei, SmartRG — un seul SDK, un seul dashboard, multi-tenant.
            <span className="text-text-faint block mt-2">Comme Stripe pour les paiements, mais pour ton infra ISP.</span>
          </p>
          <div className="mt-7 flex items-center justify-center gap-2">
            <Link to="/login" className="btn btn-primary h-9 px-4 text-sm">Console ops</Link>
            <a href="https://github.com" className="btn h-9 px-4 text-sm">Documentation</a>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="border-b border-border">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <h2 className="text-xl font-semibold mb-8 text-center">Ce qu'on fait mieux que Calix Cloud</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Feature
              icon={Activity}
              title="Historique haute résolution"
              text="Signal WiFi par client, bande passante par device, graphes 90 jours+ avec compression TimescaleDB. Trouve le PS5 qui sature à 21h en 2 secondes."
            />
            <Feature
              icon={Wifi}
              title="Mass operations"
              text="Firmware rollout en batch avec canary 10%/50%/100%, rollback auto sur fail. Reboot, config push, speedtest TR-143 sur des centaines de CPE en parallèle."
            />
            <Feature
              icon={Cpu}
              title="API-driven, multi-tenant"
              text="Tout est exposé via REST. Brancher Sonar, Telkel, ton CMS — push customers, pré-assigner devices avant livraison, auto-link au premier Inform."
            />
            <Feature
              icon={Shield}
              title="Migration sans douleur"
              text="Calix Cloud workflow ou DHCP option 43 → device bascule au prochain Inform. On coupe les 2 canaux TR-069 (Main + X_000631 Calix vendor)."
            />
            <Feature
              icon={Sparkles}
              title="UI custom (en cours)"
              text="Pas le UI Calix années 2010. Dark mode, Cmd+K, real-time, mobile-friendly. Designée par Claude pour la densité info NOC."
            />
            <Feature
              icon={Cpu}
              title="Provision auto"
              text="Customer + service plan + profils WiFi/Voice/QoS → SSID résolu + password généré + push TR-069 en 1 API call. Activation < 60s."
            />
          </div>
        </div>
      </section>

      {/* Stack */}
      <section id="stack">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <h2 className="text-xl font-semibold mb-8 text-center">Stack solide, transparente</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              ['filosACS', 'TR-069 ACS (cœur open source)'],
              ['MongoDB', 'state runtime des CPE'],
              ['PostgreSQL', 'metadata + TimescaleDB time-series'],
              ['Laravel 11', 'API REST + scheduler + jobs'],
              ['Redis', 'cache + queue + sessions'],
              ['nginx + LE', 'reverse proxy + TLS auto'],
              ['React + Vite', 'UI (ceci)'],
              ['Native systemd', 'pas de Docker en prod'],
            ].map(([name, sub]) => (
              <div key={name} className="card card-pad">
                <div className="font-medium text-sm">{name}</div>
                <div className="text-text-faint text-xs mt-0.5">{sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border bg-bg-2">
        <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between text-xs text-text-faint">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-brand animate-firefly" />
            <span>Filos · NMS</span>
          </div>
          <div>© 2026 · Self-hosted</div>
        </div>
      </footer>
    </div>
  );
}

function Feature({ icon: Icon, title, text }: { icon: any; title: string; text: string }) {
  return (
    <div className="card card-pad space-y-2">
      <div className="w-8 h-8 rounded bg-brand-dim flex items-center justify-center text-brand">
        <Icon size={15} />
      </div>
      <h3 className="font-semibold text-sm">{title}</h3>
      <p className="text-text-muted text-xs leading-relaxed">{text}</p>
    </div>
  );
}
